import React from 'react';
import { DocumentChromeVariant } from '../../config';
import type { LongDateParts } from '../../dateOrdinal';
import './document-chrome.scss';

/**
 * The fixed page-1 chrome: everything above the block content.
 *
 * Why this is a component and not inline JSX: DocumentEditor renders the chrome
 * TWICE -- once in the offscreen measurement container, whose height seeds page
 * 1's available space, and once on the visible first page. Those two copies must
 * be identical or pagination is wrong in a way that is invisible until content
 * lands on the wrong page. They used to be copy-pasted.
 *
 * The class names `.document-letterhead` and `.document-header-section` are
 * load-bearing: DocumentEditor measures the header by querying for exactly those
 * two selectors, and falls back to a hard-coded 200px if it finds neither. A
 * variant may add a modifier class but must keep the base class.
 *
 * Each variant here has a counterpart in DocumentPDF (renderLetterHeader /
 * renderSiteInstructionHeader). Change one, change both.
 *
 * The site instruction's sizes and colours come from CSS custom properties that
 * DocumentEditor applies to its ROOT (from siChromeTokens), not from this
 * component -- the body blocks need them too, and they are siblings of the
 * header rather than children.
 */
export interface DocumentChromeProps {
  variant: DocumentChromeVariant;
  /** Document date, split long ("5" / "th" / " September 2026"). */
  date: LongDateParts;
  /** Job number in dot notation for a stage ("1234.1"), plain otherwise. */
  jobNumber: string;
  /** Project description, with the stage prefix when the document is for a stage. */
  projectDescription: string;

  // --- letter only ---
  clientAddress?: React.ReactNode;
  contactDisplay?: string;
  dearDisplay?: string;
  reportTitle?: string;
  siteAddress?: string;
  /**
   * Whether the Re: block carries the project address line at all. False for a
   * template whose originals omit it; DocumentPDF is given the same value, and
   * the two must agree or the body moves a line in the export.
   */
  showSiteAddress?: boolean;

  // --- site instruction only ---
  clientArchitect?: string;
  contractor?: string;
  /** The allocated SI number, as text. Blank until one is allocated. */
  siNo?: string;
  /** How the instruction was arrived at: "On Site" / "Photographic Record". */
  method?: string;
}

/**
 * A long date with its ordinal suffix superscripted: 5<sup>th</sup> May 2026.
 *
 * The size and raise come from --date-ordinal-* (dateOrdinal.ts), applied at
 * the editor root so both chromes get them. A styled span rather than `<sup>`,
 * whose extra raise makes the line a fifth taller -- see document-chrome.scss.
 *
 * Its counterpart is renderLongDate in DocumentPDF.
 */
const LongDate: React.FC<{ parts: LongDateParts }> = ({ parts }) => (
  <>
    {parts.day}
    <span className="date-ordinal">{parts.ordinal}</span>
    {parts.rest}
  </>
);

const LETTERHEAD = (
  <div className="document-letterhead">
    <img src="/letterhead.png" alt="Terpkos Engineering Letterhead" />
  </div>
);

const LetterHeader: React.FC<DocumentChromeProps> = ({
  date,
  jobNumber,
  projectDescription,
  clientAddress,
  contactDisplay,
  dearDisplay,
  reportTitle,
  siteAddress,
  showSiteAddress = true,
}) => (
  <div className="document-header-section">
    <div className="header-row">
      <span className="header-date"><LongDate parts={date} /></span>
      <span className="header-project-no">Project No: {jobNumber}</span>
    </div>
    {clientAddress}
    <div className="header-attention">
      <span className="attention-label">{'ATTENTION:   '}</span>
      <span className={!contactDisplay ? 'placeholder' : ''}>
        {contactDisplay || '[Contact Name]'}
      </span>
    </div>
    <div className="header-dear">
      Dear{' '}
      <span className={!dearDisplay ? 'placeholder' : ''}>
        {dearDisplay || '[Name]'}
      </span>
      ,
    </div>
    <div className="header-reference">
      <span className="reference-label">Re:</span>
      <div className="reference-content">
        <div className={`report-title ${!reportTitle ? 'placeholder' : ''}`}>
          {reportTitle || '[REPORT TITLE]'}
        </div>
        <div className="project-name">{projectDescription}</div>
        {showSiteAddress && (
          <div className={`project-address ${!siteAddress ? 'placeholder' : ''}`}>
            {siteAddress || '[Project Address]'}
          </div>
        )}
      </div>
    </div>
  </div>
);

/** One labelled, underlined cell of the site instruction's field grid. */
const SiField: React.FC<{
  label: string;
  value?: string;
  placeholder: string;
  /** Rendered instead of `value` when the cell needs markup, as the date does. */
  children?: React.ReactNode;
}> = ({ label, value, placeholder, children }) => (
  <div className="si-field">
    <div className="si-field__label">{label}</div>
    <div className={`si-field__value ${!value ? 'placeholder' : ''}`}>
      {children ?? (value || placeholder)}
    </div>
  </div>
);

const SiteInstructionHeader: React.FC<DocumentChromeProps> = ({
  date,
  jobNumber,
  projectDescription,
  clientArchitect,
  contractor,
  siNo,
  method,
}) => (
  <div className="document-header-section document-header-section--si">
    <h1 className="si-title">SITE INSPECTION RECORD</h1>

    {/* Three rows of two. The right column's top cell carries the two short
        reference numbers side by side, which frees its lower cells for the date
        and the method. Mirrored by renderSiteInstructionHeader in DocumentPDF. */}
    <div className="si-fields">
      <SiField label="PROJECT" value={projectDescription} placeholder="[Project]" />
      <div className="si-field-pair">
        <SiField label="PROJECT NO" value={jobNumber} placeholder="[Project No]" />
        <SiField label="SI NO" value={siNo} placeholder="[SI No]" />
      </div>
      <SiField label="CLIENT / ARCHITECT" value={clientArchitect} placeholder="[Client]" />
      <SiField label="DATE" value={`${date.day}${date.ordinal}${date.rest}`} placeholder="[Date]">
        <LongDate parts={date} />
      </SiField>
      <SiField label="CONTRACTOR" value={contractor} placeholder="[Contractor]" />
      <SiField label="METHOD" value={method} placeholder="[Method]" />
    </div>

    {/* The REF | DESCRIPTION header belongs to the instruction list, not to
        the page: the list repeats it on every page it continues onto. */}
    <div className="si-notice">
      <p>
        In accordance with the Conditions of the Contract between the Proprietor and the
        Contractor, the Site Inspection Record set out below is hereby issued to the Contractor.
      </p>
      <p>
        Should the Contractor consider that this instruction involves any cost adjustment or
        time extension, the contractor shall inform the Superintendent before complying with
        the instruction.
      </p>
    </div>
  </div>
);

const DocumentChrome: React.FC<DocumentChromeProps> = (props) => (
  <>
    {LETTERHEAD}
    {props.variant === 'site_instruction' ? (
      <SiteInstructionHeader {...props} />
    ) : (
      <LetterHeader {...props} />
    )}
  </>
);

export default DocumentChrome;
